/**
 * Atlas PM · 系统设置 · 任务形态 · 删除二次确认弹窗。
 *
 * ⚠️ 后端为**物理删除**（无软删）。若形态被任务引用，后端返回
 *    409 DICT_IN_USE「有 N 个任务正在使用该形态，无法删除」，
 *    该 message 会原样展示在弹窗内（不吞错）。
 */

import { useEffect } from 'react';
import Button from '../../../components/Button';
import Modal from '../../../components/Modal';
import { ApiError } from '../../../api/client';
import { type TaskCategoryItem } from './api';
import { useDeleteTaskCategory } from './useTaskCategories';

export interface DeleteTaskCategoryDialogProps {
  category: TaskCategoryItem | null;
  open: boolean;
  onClose(): void;
  onDeleted?(id: string): void;
}

export default function DeleteTaskCategoryDialog({
  category,
  open,
  onClose,
  onDeleted,
}: DeleteTaskCategoryDialogProps) {
  const remove = useDeleteTaskCategory();

  // 每次打开清掉上一次的失败信息，避免跨条目串错
  useEffect(() => {
    if (open) remove.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const confirm = () => {
    if (!category) return;
    remove.mutate(category.id, {
      onSuccess: () => {
        onDeleted?.(category.id);
        onClose();
      },
    });
  };

  const errorMessage =
    remove.error instanceof ApiError
      ? remove.error.message
      : remove.error instanceof Error
        ? remove.error.message
        : null;

  return (
    <Modal
      open={open}
      size="sm"
      danger
      title={`删除任务形态 · ${category?.name ?? ''}`}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={remove.isPending}>
            取消
          </Button>
          <Button
            variant="danger"
            onClick={confirm}
            disabled={remove.isPending}
            data-testid="tc-delete-confirm"
          >
            {remove.isPending ? '删除中…' : '确认删除'}
          </Button>
        </>
      }
    >
      <div className="pm-delete-meta">
        代码：<b>{category?.code ?? '—'}</b>
      </div>

      <div className="pm-delete-explain">
        删除为物理删除，<b>不可恢复</b>。
        <br />
        若有任务正在使用该形态，系统会拒绝删除并提示引用数量，请先处理相关任务。
      </div>

      {errorMessage && (
        <div className="pm-modal__err" data-testid="tc-delete-error">
          ⚠ {errorMessage}
        </div>
      )}
    </Modal>
  );
}
